import React, { useRef, useEffect } from 'react';
import { useFullScreenHandle } from 'react-full-screen';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import bbox from '@turf/bbox';
import { StyleFullScreen, MapWrapper } from '../style/map_components';
import '../style/legend_map.css';
import { PMTILES_PREF_KEY } from '../config/config';
import { PMTILES_URL } from '../const/general';
import { setShelterLevelColor, setEvacAreaLevelColor, setShindoLevelColor } from '../func/color';
import MapFullscreenIcon from '../item/map_fullscreen_icon';
import MapSelectLayersIcon from '../item/map_select_layers_icon';
import MapShelterAnalysisLegendIcon from '../item/map_shelter_analysis_legend_icon';
import choropleth_city from '../data/city.json';

const sourceSetting = {
    "osm": {
        "type": "raster",
        "tiles": ["https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg"],
        "tileSize": 256,
        "attribution": "MapLibre | 地図の出典：<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>国土地理院</a>"
    },
    "city": {
        "type": "vector",
        "tiles": [`${PMTILES_URL}/${PMTILES_PREF_KEY}/city/{z}/{x}/{y}.mvt`]
    }
};

// 背景地図の設定
const baseLayer = [
    {
        "id": "osm-tiles",
        "type": "raster",
        "source": "osm",
        "minzoom": 0,
        "maxzoom": 24
    }
];

// PMTilesから取得して表示、非表示を制御するレイヤ達
const otherLayers = [
    {
        "id": "city",
        "source": "city",
        "source-layer": "city",
        "type": "fill",
        "paint": {
            "fill-color": "#5c9fed",
            "fill-opacity": 0.3
        },
        "layout": {
            "visibility": "visible" // 最初から表示する場合は "visible"に
        }
    },
    {
        "id": "city_line",
        "source": "city",
        "source-layer": "city",
        "type": "line",
        "paint": {
            "line-opacity": 0.6,
            "line-color": "#ffffff",
            "line-width": 1.5
        },
        "layout": {
            "visibility": "visible" // 表示制御は setLayerStyle() 側で行います
        }
    }
];

const MapComponent = (props) => {
    const mapContainer = useRef(null);
    const map = useRef(null);

    // --- ベース地図 URL 定数 ---
    const SATELLITE_URL = 'https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg';
    const PALE_URL = 'https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png';

    // ベース地図切替ハンドラ（MapSelectLayersIcon の onChangeBaseMap から呼ぶ）
    const handleBaseMapChange = (value) => {
        if (!map.current || !map.current.isStyleLoaded()) return;

        const tilesUrl = value === 'pale' ? PALE_URL : SATELLITE_URL;

        try {
            if (map.current.getLayer && map.current.getLayer('osm-tiles')) {
                map.current.removeLayer('osm-tiles');
            }
            if (map.current.getSource && map.current.getSource('osm')) {
                map.current.removeSource('osm');
            }

            const paint = value === 'pale' ? { 'raster-saturation': -1 } : {};

            map.current.addSource('osm', {
                type: 'raster',
                tiles: [tilesUrl],
                tileSize: 256,
                attribution: "MapLibre | 地図の出典：<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>国土地理院</a>"
            });

            const styleLayers = map.current.getStyle && map.current.getStyle().layers;
            const beforeId = Array.isArray(styleLayers) && styleLayers.length ? styleLayers[0].id : undefined;

            const layerDef = {
                id: 'osm-tiles',
                type: 'raster',
                source: 'osm',
                minzoom: 0,
                maxzoom: 24
            };
            if (Object.keys(paint).length) {
                layerDef.paint = paint;
            }
            map.current.addLayer(layerDef, beforeId);
        } catch (err) {
            console.error('handleBaseMapChange error:', err);
        }
    };

    // 監視用Ref
    const shelterAnalysisDataRef = useRef(props.shelterList);
    const shelterEvacLevelCityDataRef = useRef(props.shelterEvacLevelCityList);
    // マップから点検が必要な避難所を非表示
    // const shelterNeedInspectionListRef = useRef(props.shelterNeedInspectionList);
    const selectCitysRef = useRef(props.selectCitys);
    const selectedShelterRef = useRef(props.selectShelterCode);

    const mapStyle = {
        height: '100%',
        width: '100%',
        margin: '0 auto',
    }

    // 全画面表示切り替え
    const fullscreenHandle = useFullScreenHandle()

    const layersSetting = [...baseLayer, ...otherLayers]

    let hoveredId = null

    const checkData = (data) => {
        if (!data || data === undefined || data === null || data.length < 1) {
            return false
        }
        return true
    }

    const mergeGeoJSON = (geo1, geo2) => {
        return {
            type: "FeatureCollection",
            features: [
                ...(geo1?.features || []),
                ...(geo2?.features || [])
            ]
        };
    };

    // レイヤの色分け
    const setLayerStyle = () => {
        // フィルター済みのクロノロジーデータ
        if (map.current && map.current.isStyleLoaded()) {
            // クロノロジーデータ表示
            addShelterAnalysisLayer();
            // クロノロジーデータを GeoJSON に変換
            const geo = buildShelterAnalysisGeoJSON(shelterAnalysisDataRef.current);
            // マップから点検が必要な避難所を非表示
            // const geoBuilding = buildShelterInspection(shelterNeedInspectionListRef.current);
            const geoBuilding = [];
            if(geo?.length === 0 && geoBuilding?.length === 0) return;
            const mergedGeo = mergeGeoJSON(geo, geoBuilding);
            map.current.getSource('shelter_analysis').setData(mergedGeo);
            // クロノロジーの色分け
            setShelterAnalysisStyle();

            // 市町村レイヤの色分け
            setCityFillByStatus();
            if (checkData(mergedGeo)) {
                setMapCenterZoom(mergedGeo);
            }
        }
    }

    // 避難所スタイル変更
    const setShelterAnalysisStyle = () => {
        if (!map.current) return;

        map.current.setPaintProperty(
            'shelter_analysis',
            'circle-color',
            [
                'case',
                ['has', 'color'],
                ['get', 'color'],
                'rgba(0,0,0,0)'
            ]
        );

        map.current.setPaintProperty(
            'shelter_analysis',
            'circle-stroke-color',
            '#ffffff'
        );
    };

    // 市町村レイヤスタイル変更
    const setCityFillByStatus = () => {
        if (!map.current || !map.current.getLayer || !map.current.getLayer('city')) return;
        const cityData = shelterEvacLevelCityDataRef.current || [];

        // ベクターフィーチャーの候補プロパティを数値で正規化して参照する
        const matchExpr = [
            'match',
            ['coalesce',
                ['to-number', ['get', 'id']],
                ['to-number', ['get', 'code']],
                ['to-number', ['get', 'city_code']]
            ]
        ];

        for (const it of cityData) {
            const cid = Number(it.city_code);
            if (Number.isNaN(cid)) continue;
            const level = Number(it.int_alertlv_num ?? it.status_category ?? 0);
            const color = (typeof setEvacAreaLevelColor === 'function')
                ? setEvacAreaLevelColor(level)
                : (Number(level) === 2 ? '#ff0000' : Number(level) === 1 ? '#ffa500' : '#5c9fed');
            matchExpr.push(cid, color);
        }

        matchExpr.push('rgba(0,0,0,0)');

        try {
            map.current.setPaintProperty('city', 'fill-color', matchExpr);
        } catch (e) {
            console.error('setCityFillByStatus error', e);
        }
    };

    // 地図のセンターとズーム調整
    const setMapCenterZoom = (geo) => {
        if (!map.current) return;
        if (geo.features.length === 0) return;
        if (!geo.type) {
            geo.type = "FeatureCollection";
        }

        // Turf.js で bbox を取得
        const bounds = bbox(geo); // [west, south, east, north]

        map.current.fitBounds(bounds, {
            padding: 30,
            maxZoom: 13,   // 拡大制限
            duration: 800  // アニメーション時間
        });
    }

    // --- クロノロジー用 GeoJSON 作成 ---
    const buildShelterAnalysisGeoJSON = (list) => {
        const fc = { features: [] };
        if (!Array.isArray(list) || list.length === 0) return [];
        const featuresList = list.map(item => {
            const lat = item.dc_lat_num ?? null;
            const lon = item.dc_lng_num ?? null;
            if (lat == null || lon == null) return null;
            const coordsLon = Number(lon);
            const coordsLat = Number(lat);
            if (!Number.isFinite(coordsLon) || !Number.isFinite(coordsLat)) return null;
            return {
                type: "analysis",
                geometry: {
                    type: "Point",
                    coordinates: [coordsLon, coordsLat]
                },
                properties: {
                    id: item.id,
                    code: item.code,
                    city_code: item.city_code,
                    city_name: item.txt_city_name,
                    area_name: item.txt_area_name,
                    sort_name: item.txt_sort_name,
                    status_category: item.status_category,
                    status_category_name: item.status_category_name,
                    open_time: item.open_time,
                    close_time: item.close_time,
                    color: setShelterLevelColor(item.status_category_name)
                }
            };
        }).filter(Boolean);
        fc.features = featuresList;
        return fc;
    };

    // --- クロノロジー用 GeoJSON 作成 ---
    const buildShelterInspection = (list) => {
        const fc = { features: [] };
        if (!Array.isArray(list) || list.length === 0) return [];
        const featuresList = list.map(item => {
            const lat = item.latitude ?? null;
            const lon = item.longitude ?? null;
            if (lat == null || lon == null) return null;
            const coordsLon = Number(lon);
            const coordsLat = Number(lat);
            if (!Number.isFinite(coordsLon) || !Number.isFinite(coordsLat)) return null;

            return {
                type: "inspection",
                geometry: {
                    type: "Point",
                    coordinates: [coordsLon, coordsLat]
                },
                properties: {
                    code: item.code,
                    ctv_code: item.ctv_code,
                    city_name: item.txt_city_name,
                    area_name: item.txt_area_name,
                    shindo: item.shindo,
                    building_name: item.building_name,
                    color: setShindoLevelColor(item.shindo)
                }
            };
        }).filter(Boolean);
        fc.features = featuresList;
        return fc;
    };

    // --- Map 避難所用ソース & 市町村レイヤを追加 ---
    const addShelterAnalysisLayer = () => {
        if (!map.current || !map.current.isStyleLoaded()) return;
        if (map.current.getSource('shelter_analysis')) return;

        map.current.addSource('shelter_analysis', {
            type: 'geojson',
            data: { type: "FeatureCollection", features: [] }
        });

        map.current.addLayer({
            id: 'shelter_analysis',
            type: 'circle',
            source: 'shelter_analysis',
            paint: {
                'circle-radius': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    5, 6,
                    10, 8,
                    12, 10,
                    15, 12,
                    18, 14
                ],
                'circle-stroke-width': 1.0,
                'circle-stroke-color': '#ffffff',
                'circle-color': '#0000FF',
                'circle-opacity': 1
            },
            layout: {
                visibility: 'visible'
            }
        });

        // 優先順位: shelter -> city
        map.current.on('click', (e) => {
            try {
                const shelterFeatures = map.current.queryRenderedFeatures(e.point, { layers: ['shelter_analysis'] });
                if (shelterFeatures && shelterFeatures.length > 0) {
                    handleShelterClick({ features: shelterFeatures });
                    return;
                }
                const cityFeatures = map.current.queryRenderedFeatures(e.point, { layers: ['city'] });
                if (cityFeatures && cityFeatures.length > 0) {
                    handleCityClick({ features: cityFeatures });
                    return;
                }
            } catch (err) {
                console.error('map click handler error', err);
            }
        });

        map.current.on('mousemove', (e) => {
            try {
                // shelter を優先してポップアップ表示
                const shelterFeatures = map.current.queryRenderedFeatures(e.point, { layers: ['shelter_analysis'] });
                if (shelterFeatures && shelterFeatures.length > 0) {
                    handleMouseMove({ features: shelterFeatures, lngLat: e.lngLat });
                    return;
                }
                // shelter がなければ city のホバー処理
                const cityFeatures = map.current.queryRenderedFeatures(e.point, { layers: ['city'] });
                if (cityFeatures && cityFeatures.length > 0) {
                    handleCityMouseMove({ features: cityFeatures, lngLat: e.lngLat });
                    return;
                }
                // どちらも無ければマウス離脱処理
                handleMouseLeave();
            } catch (err) {
                console.error('map mousemove handler error', err);
            }
        });
    };

    // マウスホバー時の処理
    const handleMouseMove = (e) => {
        const feature = e.features && e.features[0];
        if (!feature) return;
        const props = feature.properties || {};
        const id = props.id;
        if (id === undefined || id === null) return;

        // すでにホバー中なら無視
        if (hoveredId === id) return;

        const shelterAnalysisData = shelterAnalysisDataRef.current;
        if (!Array.isArray(shelterAnalysisData) || shelterAnalysisData.length === 0) return;

        const data = shelterAnalysisData.find(item => item.id === Number(id) || item.id === id);

        // 前のポップアップを削除
        if (map.current.__hoverPopup) {
            map.current.__hoverPopup.remove();
            map.current.__hoverPopup = null;
        }

        if (!data) {
            map.current.getCanvas().style.cursor = '';
            return;
        } else {
            map.current.getCanvas().style.cursor = 'pointer';
        }
        hoveredId = id;

        const popup = new maplibregl.Popup({
            closeButton: false,
            closeOnClick: false
        })
            .setLngLat(e.lngLat)
            .setHTML(`
                <div>避難所名：<strong>${data.txt_area_name}</strong></div>
                <div>status_category：<strong>${data.status_category}</strong></div>
                <div>避難所カテゴリ：<strong>${data.status_category_name}</strong></div>
            `)
            .addClassName('custom-popup')
            .addTo(map.current);

        map.current.__hoverPopup = popup;
    };

    // 市町村レイヤーホバー時の処理
    const handleCityMouseMove = (e) => {
        const feature = e.features && e.features[0];
        if (!feature) return;
        const propsF = feature.properties || {};
        const cityName = propsF.name || propsF.city_name || '不明';
        const cityId = propsF.id ?? propsF.code ?? propsF.city_code;
        if (cityId === undefined || cityId === null) return;

        // すでにホバー中なら無視
        if (hoveredId === `city_${cityId}`) return;

        const shelterData = shelterEvacLevelCityDataRef.current || [];
        // city_code フィールドと比較（数値/文字列どちらも対応）
        const cityNum = Number(cityId);
        const matched = shelterData.filter(item =>
            !Number.isNaN(cityNum)
                ? Number(item.city_code) === cityNum
                : String(item.city_code) === String(cityId)
        );

        // 警戒レベルは該当避難所の最大 status_category を採用（ない場合 0）
        const level = matched.length > 0
            ? Math.max(...matched.map(it => Number(it.status_category || 0)))
            : 0;

        // 既存ポップアップ削除
        if (map.current?.__hoverPopup) {
            map.current.__hoverPopup.remove();
            map.current.__hoverPopup = null;
        }

        if (map.current?.getCanvas) map.current.getCanvas().style.cursor = 'pointer';
        hoveredId = `city_${cityId}`;

        const popup = new maplibregl.Popup({ closeButton: false, closeOnClick: false })
            .setLngLat(e.lngLat)
            .setHTML(`
                <div>市町村：<strong>${cityName}</strong></div>
                <div>警戒レベル：<strong>${level}</strong></div>
            `)
            .addClassName('custom-popup')
            .addTo(map.current);

        map.current.__hoverPopup = popup;
    };

    // レイヤーマウスアウト時の処理
    const handleMouseLeave = () => {
        map.current.getCanvas().style.cursor = '';
        if (map.current.__hoverPopup) {
            map.current.__hoverPopup.remove();
            map.current.__hoverPopup = null;
            hoveredId = null;
        }
    };

    // 避難所レイヤクリック時の処理
    const handleShelterClick = (e) => {
        if (!e || !e.features || e.features.length === 0) return;

        const feature = e.features[0];
        if (!feature || !feature.properties) return;

        const featureId = feature.properties.id ?? null;
        if (featureId == null) return;

        props.setSelectShelterCode([featureId]);
        props.setSelectSubCity([]);
    };

    // 市町村レイヤクリック時の処理
    const handleCityClick = (e) => {
        if (!e || !e.features || e.features.length === 0) return;

        const feature = e.features[0];
        if (!feature || !feature.properties) return;

        const featureId = feature.properties.id ?? null;
        if (featureId == null) return;

        props.setSelectSubCity([featureId]);
        props.setSelectShelterCode([]);

        // choropleth_city から該当市町村フィーチャを探して bbox でズーム
        const matchIdNum = Number(featureId);
        const cityFeature = choropleth_city.features.find(f => {
            const raw = f.properties?.id;
            if (Array.isArray(raw)) {
                return raw.some(r => Number(r) === matchIdNum);
            }
            return Number(raw) === matchIdNum;
        });

        if (cityFeature) {
            const cityGeo = { type: "FeatureCollection", features: [cityFeature] };
            setMapCenterZoom(cityGeo);
        }
    };

    // ロード時の処理
    const handleLoad = () => {
        if (!map.current) return;
        // レイヤ色付け
        setLayerStyle();
    };

    // マップ初期化処理
    useEffect(() => {
        if (map.current) return;
        map.current = new maplibregl.Map({
            container: mapContainer.current,
            center: props.riskMapCenter,
            zoom: props.riskMapZoom,
            style: {
                version: 8,
                sources: sourceSetting,
                layers: layersSetting
            },
            attributionControl: false
        });
        map.current.addControl(new maplibregl.NavigationControl({
            // コンパス表示なし
            showCompass: false,
            showZoom: true
        }), 'top-left');
        map.current.addControl(new maplibregl.AttributionControl({
            // Attributionは折りたたまない
            compact: false,
        }), 'bottom-right');
        map.current.on('load', handleLoad);

        return () => {
            if (!map.current) return;
            map.current.off('load', handleLoad);

            // shelter_analysis 用イベント解除（named handlers を off）
            try {
                map.current.off('mousemove', 'shelter_analysis', handleMouseMove);
                map.current.off('mousemove', 'city', handleCityMouseMove);
                map.current.off('mouseleave', 'shelter_analysis', handleMouseLeave);
                map.current.off('click', 'shelter_analysis', handleShelterClick);
                map.current.off('click', 'city', handleCityClick);
            } catch (e) { }

            // city レイヤー用イベント解除（named handlers を off）
            try {
                map.current.off('mousemove', 'city', handleCityMouseMove);
            } catch (e) { }

            // canvas のグローバル mouseleave
            map.current.getCanvas().removeEventListener('mouseleave', handleMouseLeave);

            map.current.remove();
            map.current = null;
        };
    }, []);

    useEffect(() => {
        // 更新用に ref も更新
        shelterAnalysisDataRef.current = props.shelterList
        shelterEvacLevelCityDataRef.current = props.shelterEvacLevelCityList
        // マップから点検が必要な避難所を非表示
        // shelterNeedInspectionListRef.current = props.shelterNeedInspectionList
        selectedShelterRef.current = props.selectShelterCode

        if (!map.current || !map.current.isStyleLoaded()) return;

        const geo = buildShelterAnalysisGeoJSON(shelterAnalysisDataRef.current);
        // マップから点検が必要な避難所を非表示
        // const geoBuilding = buildShelterInspection(shelterNeedInspectionListRef.current);
        const geoBuilding = [];
        const mergedGeo = mergeGeoJSON(geo, geoBuilding);

        // ソースが存在すればデータをセット、なければ作る
        try {
            if (map.current.getSource && map.current.getSource('shelter_analysis')) {
                map.current.getSource('shelter_analysis').setData(mergedGeo);
            } else {
                map.current.addSource('shelter_analysis', {
                    type: 'geojson',
                    data: mergedGeo
                });
                // レイヤーが存在しなければ addLayer する（既に追加済みならスキップ）
                if (!map.current.getLayer || !map.current.getLayer('shelter_analysis')) {
                    map.current.addLayer({
                        id: 'shelter_analysis',
                        type: 'circle',
                        source: 'shelter_analysis',
                        paint: {
                            'circle-radius': [
                                'interpolate',
                                ['linear'],
                                ['zoom'],
                                5, 6,
                                10, 8,
                                12, 10,
                                15, 12,
                                18, 14
                            ],
                            'circle-stroke-width': 1.0,
                            'circle-stroke-color': '#ffffff',
                            'circle-color': '#0000FF',
                            'circle-opacity': 1
                        },
                        layout: { visibility: 'visible' }
                    });
                }
            }
        } catch (err) {
            console.error('update shelter_analysis source error:', err);
        }

        // 色分けや stroke の更新
        try {
            setShelterAnalysisStyle();
        } catch (err) {
            console.error('setShelterAnalysisStyle error:', err);
        }

        // 表示用に中心とズームも調整（要素があれば）
        if (geo && geo.features && geo.features.length > 0) {
            setMapCenterZoom(geo);
        }
    }, [props.shelterList, props.shelterEvacLevelCityList, props.selectShelterCode]);

    // 市町村チェックボックス変更時に呼ばれる
    useEffect(() => {
        selectCitysRef.current = props.selectCitys

        // レイヤの色分け、中心・ズーム調整
        setLayerStyle()
    }, [props.selectCitys]);

    // 市町村チェックボックス変更時に呼ばれる
    useEffect(() => {
        selectedShelterRef.current = props.selectShelterCode
    }, [props.selectShelterCode]);

    // 地域レイヤークリック時に呼ばれる
    useEffect(() => {
        shelterAnalysisDataRef.current = props.shelterList
        shelterEvacLevelCityDataRef.current = props.shelterEvacLevelCityList
        // マップから点検が必要な避難所を非表示
        // shelterNeedInspectionListRef.current = props.shelterNeedInspectionList

        if (!map.current) return;
        const interval = setInterval(() => {
            if (map.current?.isStyleLoaded()) {
                setLayerStyle();
                clearInterval(interval);
            }
        }, 100);

        return () => clearInterval(interval);
    }, [
        props.shelterList, props.shelterEvacLevelCityList, props.selectShelterCode, props.shelterNeedInspectionList
    ]);

    return (
        <StyleFullScreen
            handle={fullscreenHandle}
            enabled={true}
        >
            <MapWrapper>
                <div
                    ref={mapContainer}
                    className=""
                    style={mapStyle} />
                <MapFullscreenIcon
                    fullscreenHandle={fullscreenHandle}
                    top={'70px'}
                />
                <MapSelectLayersIcon
                    onChangeBaseMap={handleBaseMapChange}
                    pageType="sheleter_analysis"
                />
                <MapShelterAnalysisLegendIcon />
            </MapWrapper>
        </StyleFullScreen>
    );
};
export default MapComponent;